'use client';
import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { ArrowLeft, Copy, Check, RefreshCw, Palette } from 'lucide-react';

export default function ColorPalettePage() {
  const [colors, setColors] = useState<string[]>(['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981']);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const generatePalette = () => {
    const newColors = Array.from({ length: 5 }, () => {
      return '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0').toUpperCase();
    });
    setColors(newColors);
  };

  const copyToClipboard = (color: string, index: number) => {
    navigator.clipboard.writeText(color);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-5xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Color Palette Generator
              </h1>
            <p className="text-lg text-muted-foreground">
              Generate beautiful color palettes for your data visualizations
            </p>
          </div>

          <div className="bg-card rounded-xl border p-8 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
              {colors.map((color, index) => (
                <div key={index} className="space-y-3">
                  <div 
                    className="h-32 rounded-lg shadow-inner transition-transform hover:scale-105 cursor-pointer"
                    style={{ backgroundColor: color }}
                    onClick={() => copyToClipboard(color, index)}
                  />
                  <div className="flex items-center justify-between px-1">
                    <span className="font-mono font-bold">{color}</span>
                    <button 
                      onClick={() => copyToClipboard(color, index)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {copiedIndex === index ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <Button 
              onClick={generatePalette} 
              className="w-full py-6 text-lg"
              size="lg"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Generate New Palette
            </Button>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-card rounded-lg border">
              <h3 className="font-semibold mb-3 flex items-center">
                <Palette className="w-5 h-5 mr-2 text-blue-500" />
                Why Color Matters
              </h3>
              <p className="text-sm text-muted-foreground">
                Choosing the right colors is crucial for data visualization. It helps distinguish between different data series and can convey meaning (e.g., red for danger, green for success).
              </p>
            </div>
            <div className="p-6 bg-card rounded-lg border">
              <h3 className="font-semibold mb-3 flex items-center">
                <Check className="w-5 h-5 mr-2 text-green-500" />
                Accessibility Tip
              </h3>
              <p className="text-sm text-muted-foreground">
                Ensure your color choices have enough contrast and are color-blind friendly to make your charts accessible to everyone.
              </p>
            </div>
          </div>
        </div>
      </main>

      <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold mb-4">Color Palette FAQs</h2>
        <div className="space-y-6">
          <div><p className="font-semibold">1. How do I use these colors in my charts?</p><p className="text-muted-foreground">Click on any color hex code to copy it, then paste it into your charting library's color configuration.</p></div>
          <div><p className="font-semibold">2. Are these palettes random?</p><p className="text-muted-foreground">Yes, the "Generate New Palette" button creates a completely random set of 5 colors.</p></div>
          <div><p className="font-semibold">3. Can I save my palette?</p><p className="text-muted-foreground">Currently, you can copy the hex codes. We recommend saving them in a text file or your project's theme configuration.</p></div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
